/**
 * PLP mobile toolbar + filter drawer controls
 *
 * - Top Filter → Dawn side drawer (accordion filters)
 * - Sticky Filter → same drawer as bottom sheet
 * - Sticky bar only when top toolbar leaves the viewport
 * - Accordion only (no nested submenu drawers)
 * - Close via X, backdrop / empty space, or Escape
 */
(function () {
  const SECTION_SELECTOR = '.plp-collection-section, .plp-search-section';
  const TOOLBAR_SELECTOR = '[data-plp-toolbar]';
  const STICKY_SELECTOR = '[data-plp-sticky-bar]';
  const OPEN_FILTERS_SELECTOR = '[data-plp-open-filters]';
  const CLOSE_FILTERS_SELECTOR = '[data-plp-close-filters]';
  const SORT_SELECT_SELECTOR = '[data-plp-sort-select]';
  const SORT_MIRROR_SELECTOR = '[data-plp-sort-mirror]';
  const DAWN_TRIGGER_SELECTOR = '[data-plp-dawn-filter-trigger]';
  const FACETS_ROOT_SELECTOR = '.plp-mobile-facets';
  const DAWN_DETAILS_SELECTOR = '.plp-mobile-facets .mobile-facets__disclosure';
  const FILTER_DETAILS_SELECTOR = '.plp-mobile-facets details.mobile-facets__details.js-filter';
  const MOBILE_FORM_SELECTOR = '.plp-mobile-facets #FacetFiltersFormMobile';
  const ACTIVE_FACETS_SELECTOR = '.plp-mobile-facets__active';
  const SHEET_MODE_CLASS = 'plp-filters-sheet-mode';
  const SHEET_ANIMATION_MS = 360;

  let observer = null;
  let toolbarOutOfView = false;
  let detailsObserver = null;
  let sheetModeClearTimer = null;
  let ignoreBackdropUntil = 0;
  let drawerToggleHandler = null;

  function getSection() {
    return document.querySelector(SECTION_SELECTOR);
  }

  /** PLP mobile/tablet filter UI — ≤980px (matches plp-collection.css). */
  function isMobile() {
    return window.matchMedia('(max-width: 980px)').matches;
  }

  function getDawnDetails() {
    return document.querySelector(DAWN_DETAILS_SELECTOR);
  }

  function getFacetsRoot() {
    return document.querySelector(FACETS_ROOT_SELECTOR);
  }

  function getMobileForm() {
    return document.querySelector(MOBILE_FORM_SELECTOR);
  }

  function getTrigger() {
    return document.querySelector(DAWN_TRIGGER_SELECTOR);
  }

  function isDrawerOpen() {
    const details = getDawnDetails();
    return Boolean(details && details.hasAttribute('open'));
  }

  function getDrawerInner() {
    return document.querySelector(`${FACETS_ROOT_SELECTOR} .mobile-facets__inner`);
  }

  /**
   * Toggle bottom-sheet layout. When enabling, kill transform transition for one
   * frame so we don't animate from Dawn's translateX(105vw) → translateY (glitch).
   */
  function setSheetMode(enabled) {
    if (sheetModeClearTimer) {
      window.clearTimeout(sheetModeClearTimer);
      sheetModeClearTimer = null;
    }

    const inner = getDrawerInner();
    const turningOn = Boolean(enabled) && !document.body.classList.contains(SHEET_MODE_CLASS);

    if (turningOn && inner) {
      const prevTransition = inner.style.transition;
      inner.style.transition = 'none';
      document.body.classList.add(SHEET_MODE_CLASS);
      // Force closed sheet position without interpolating from translateX
      void inner.offsetWidth;
      inner.style.transition = prevTransition;
      return;
    }

    document.body.classList.toggle(SHEET_MODE_CLASS, Boolean(enabled));
  }

  /** Keep sheet-mode class until slide-down finishes so close animates correctly. */
  function clearSheetModeAfterAnimation() {
    if (sheetModeClearTimer) {
      window.clearTimeout(sheetModeClearTimer);
    }
    sheetModeClearTimer = window.setTimeout(() => {
      const inner = getDrawerInner();
      if (inner) {
        const prevTransition = inner.style.transition;
        inner.style.transition = 'none';
        document.body.classList.remove(SHEET_MODE_CLASS);
        void inner.offsetWidth;
        inner.style.transition = prevTransition;
      } else {
        document.body.classList.remove(SHEET_MODE_CLASS);
      }
      sheetModeClearTimer = null;
      ensureAccordionOpen();
    }, SHEET_ANIMATION_MS);
  }

  /** Keep filter groups expanded like desktop (MenuDrawer strips open on close). */
  function ensureAccordionOpen() {
    window.FacetAccordion?.openAllMobile?.(document, false);
  }

  function syncActiveFacetsEmptyState() {
    const active = document.querySelector(ACTIVE_FACETS_SELECTOR);
    if (!active) return;
    const hasPills = Boolean(active.querySelector('.active-facets__button'));
    active.classList.toggle('plp-mobile-facets__active--empty', !hasPills);
  }

  function openMenuDrawerApi(details, trigger) {
    const menuDrawer = getFacetsRoot();
    details.setAttribute('open', '');
    trigger.setAttribute('aria-expanded', 'true');

    if (menuDrawer && typeof menuDrawer.openMenuDrawer === 'function') {
      menuDrawer.openMenuDrawer(trigger);
    } else {
      // Fallback if MenuDrawer API is unavailable
      requestAnimationFrame(() => {
        details.classList.add('menu-opening');
      });
      document.body.classList.add('overflow-hidden-mobile');
    }

    // Guarantee sheet/side panel becomes visible even if menu-opening is delayed
    requestAnimationFrame(() => {
      details.classList.add('menu-opening');
    });
  }

  /** Clear scroll-lock / focus trap / sheet overlay so the page is usable again. */
  function unlockPage() {
    if (sheetModeClearTimer) {
      window.clearTimeout(sheetModeClearTimer);
      sheetModeClearTimer = null;
    }

    document.body.classList.remove('overflow-hidden-mobile');
    document.body.classList.remove(SHEET_MODE_CLASS);
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }
  }

  /**
   * Hard-close the Dawn drawer. Needed after filter AJAX: Dawn's onFocusOut can call
   * closeMenuDrawer() with no event, which returns early and leaves overflow-hidden-mobile
   * + an open overlay that freezes the page.
   */
  function forceCloseDrawer() {
    const details = getDawnDetails();
    const trigger = getTrigger();
    const menuDrawer = getFacetsRoot();
    const wasOpen = Boolean(details && details.hasAttribute('open'));

    if (wasOpen && menuDrawer && typeof menuDrawer.closeMenuDrawer === 'function') {
      try {
        menuDrawer.closeMenuDrawer(new Event('click'), trigger || false);
      } catch (e) {
        // fall through to manual cleanup
      }
    }

    if (details) {
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
    }
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    unlockPage();
    ensureAccordionOpen();
    updateStickyVisibility();
  }

  function closeMenuDrawerApi(details, trigger) {
    const menuDrawer = getFacetsRoot();
    const wasSheet = document.body.classList.contains(SHEET_MODE_CLASS);

    if (menuDrawer && typeof menuDrawer.closeMenuDrawer === 'function') {
      menuDrawer.closeMenuDrawer(new Event('click'), trigger);
    } else if (details) {
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    // Always clear scroll lock — don't rely only on Dawn cleanup
    document.body.classList.remove('overflow-hidden-mobile');

    if (wasSheet) {
      clearSheetModeAfterAnimation();
    } else {
      setSheetMode(false);
      window.setTimeout(ensureAccordionOpen, SHEET_ANIMATION_MS);
    }

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(trigger || null);
    }
  }

  function closeDrawer() {
    const details = getDawnDetails();
    const trigger = getTrigger();

    if (!details || !details.hasAttribute('open')) {
      unlockPage();
      ensureAccordionOpen();
      return;
    }

    closeMenuDrawerApi(details, trigger);
  }

  function openDawnDrawer(mode) {
    const details = getDawnDetails();
    const trigger = getTrigger();
    if (!details || !trigger) return;

    const wantSheet = mode === 'sheet';
    const wasOpen = details.hasAttribute('open');
    const wasSheet = document.body.classList.contains(SHEET_MODE_CLASS);

    if (wasOpen) {
      if ((wantSheet && wasSheet) || (!wantSheet && !wasSheet)) {
        closeDrawer();
        return;
      }
      setSheetMode(wantSheet);
      details.classList.add('menu-opening');
      ensureAccordionOpen();
      return;
    }

    // Apply sheet layout before open so enter animation uses translateY
    setSheetMode(wantSheet);
    ensureAccordionOpen();
    ignoreBackdropUntil = Date.now() + 450;
    openMenuDrawerApi(details, trigger);
    ensureAccordionOpen();
  }

  function syncMirrorFromMain() {
    const main = document.querySelector(SORT_SELECT_SELECTOR);
    const mirror = document.querySelector(SORT_MIRROR_SELECTOR);
    if (!main || !mirror) return;
    if (mirror.value !== main.value) mirror.value = main.value;
  }

  function syncMainFromMirror(mirror) {
    const main = document.querySelector(SORT_SELECT_SELECTOR);
    if (!main || !mirror) return;

    if (main.value !== mirror.value) {
      main.value = mirror.value;
      main.dispatchEvent(new Event('input', { bubbles: true }));
      main.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function updateStickyVisibility() {
    const sticky = document.querySelector(STICKY_SELECTOR);
    if (!sticky) return;

    if (!isMobile()) {
      sticky.hidden = true;
      sticky.classList.remove('is-visible');
      document.body.classList.remove('plp-sticky-bar-active');
      return;
    }

    // Only when the top Sort/Filter toolbar leaves the viewport — not when drawer opens
    const shouldShow = toolbarOutOfView;

    if (shouldShow) {
      sticky.hidden = false;
      sticky.classList.add('is-visible');
      document.body.classList.add('plp-sticky-bar-active');
    } else {
      sticky.classList.remove('is-visible');
      sticky.hidden = true;
      document.body.classList.remove('plp-sticky-bar-active');
    }
  }

  function bindObserver() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    const toolbar = document.querySelector(TOOLBAR_SELECTOR);
    const sticky = document.querySelector(STICKY_SELECTOR);
    if (!toolbar || !sticky) return;

    if (!isMobile()) {
      toolbarOutOfView = false;
      updateStickyVisibility();
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        toolbarOutOfView = !entry.isIntersecting;
        updateStickyVisibility();
      },
      { root: null, threshold: 0, rootMargin: '0px' }
    );

    observer.observe(toolbar);
  }

  function bindDrawerWatcher() {
    const details = getDawnDetails();
    if (!details) return;

    if (detailsObserver) {
      detailsObserver.disconnect();
      detailsObserver = null;
    }

    if (drawerToggleHandler) {
      details.removeEventListener('toggle', drawerToggleHandler);
      drawerToggleHandler = null;
    }

    drawerToggleHandler = () => {
      if (!details.hasAttribute('open')) {
        if (
          document.body.classList.contains(SHEET_MODE_CLASS) &&
          !details.classList.contains('menu-opening') &&
          !sheetModeClearTimer
        ) {
          clearSheetModeAfterAnimation();
        } else if (!document.body.classList.contains(SHEET_MODE_CLASS)) {
          ensureAccordionOpen();
        }
      }
      updateStickyVisibility();
    };

    details.addEventListener('toggle', drawerToggleHandler);
    detailsObserver = new MutationObserver(drawerToggleHandler);
    detailsObserver.observe(details, { attributes: true, attributeFilter: ['open', 'class'] });
  }

  /**
   * Block Dawn MenuDrawer nested-submenu behavior so filters accordion
   * like desktop (native <details> toggle only).
   * Also wire close / backdrop without MenuDrawer close-button side effects.
   */
  function bindFacetsInteractions() {
    const root = getFacetsRoot();
    if (!root) return;

    if (root.dataset.plpAccordionBound !== 'true') {
      root.dataset.plpAccordionBound = 'true';

      root.addEventListener(
        'click',
        (event) => {
          const closeBtn = event.target.closest(CLOSE_FILTERS_SELECTOR);
          if (closeBtn && root.contains(closeBtn)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            closeDrawer();
            return;
          }

          const nestedSummary = event.target.closest('.mobile-facets__details > summary');
          if (nestedSummary && root.contains(nestedSummary)) {
            event.stopImmediatePropagation();
          }
        },
        true
      );
    }

    const form = getMobileForm();
    if (form && form.dataset.plpBackdropBound !== 'true') {
      form.dataset.plpBackdropBound = 'true';
      form.addEventListener('click', (event) => {
        if (event.target !== form) return;
        if (Date.now() < ignoreBackdropUntil) return;
        closeDrawer();
      });
    }
  }

  function bindEvents(section) {
    if (!section) return;

    if (section.dataset.plpToolbarBound !== 'true') {
      section.dataset.plpToolbarBound = 'true';

      section.addEventListener('click', (event) => {
        const btn = event.target.closest(OPEN_FILTERS_SELECTOR);
        if (!btn || !section.contains(btn)) return;
        event.preventDefault();
        event.stopPropagation();
        const mode = btn.getAttribute('data-plp-open-filters') || 'dawn';
        openDawnDrawer(mode);
      });

      section.addEventListener('change', (event) => {
        if (event.target.matches(SORT_MIRROR_SELECTOR)) {
          syncMainFromMirror(event.target);
        }
        if (event.target.matches(SORT_SELECT_SELECTOR)) {
          syncMirrorFromMain();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && isDrawerOpen()) {
          closeDrawer();
        }
      });

      window.addEventListener('resize', bindObserver);
    }

    syncMirrorFromMain();
    ensureAccordionOpen();
    syncActiveFacetsEmptyState();
    bindFacetsInteractions();
    bindDrawerWatcher();
    bindObserver();
  }

  function init() {
    const section = getSection();
    if (!section) return;
    bindEvents(section);
  }

  function patchFacetsRender() {
    // Before facets.js runs, FacetFiltersForm is the <form id="FacetFiltersForm"> element (named access), not the class
    if (typeof FacetFiltersForm !== 'function') return;
    if (FacetFiltersForm.__plpToolbarPatched) return;

    const prevGrid = FacetFiltersForm.renderProductGridContainer.bind(FacetFiltersForm);
    FacetFiltersForm.renderProductGridContainer = function (html) {
      prevGrid(html);
      // Filter apply closes the sticky/side drawer — unlock scroll & clear overlays
      forceCloseDrawer();
      syncMirrorFromMain();
      bindObserver();
    };

    const prevFilters = FacetFiltersForm.renderFilters.bind(FacetFiltersForm);
    FacetFiltersForm.renderFilters = function (html, event) {
      prevFilters(html, event);
      forceCloseDrawer();
      ensureAccordionOpen();
      syncActiveFacetsEmptyState();
      bindFacetsInteractions();
      bindDrawerWatcher();
    };

    const prevActive = FacetFiltersForm.renderActiveFacets.bind(FacetFiltersForm);
    FacetFiltersForm.renderActiveFacets = function (html) {
      prevActive(html);
      syncActiveFacetsEmptyState();
    };

    FacetFiltersForm.__plpToolbarPatched = true;
  }

  document.addEventListener('DOMContentLoaded', () => {
    patchFacetsRender();
    init();
  });

  if (document.readyState !== 'loading') {
    patchFacetsRender();
    init();
  }
})();
